import { OrderTrackingShipmentStatus } from "@redotech/order-tracking-common/order-tracking-types";
import {
  getTrackingMapLocation,
  locationToCityStateLabel,
  locationToQuery,
} from "@redotech/order-tracking-common/tracking-map-location";
import {
  DEFAULT_TRACKING_MAP_HEIGHT_PX,
  DEFAULT_TRACKING_MAP_ZOOM,
  TrackingMapPinShape,
} from "@redotech/order-tracking-common/tracking-page-definition";
import type {
  TrackingPageOrder,
  TrackingPageShipment,
} from "@redotech/order-tracking-common/tracking-page-order-definition";
import { applyTrackingLocationRules } from "@redotech/order-tracking-common/tracking-text-rules";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import { AdvancedMarker, APIProvider, Map } from "@vis.gl/react-google-maps";
import { memo, useContext, useMemo, useState } from "react";
import { OverflowTooltip } from "../../../arbiter-components/tooltip/overflow-tooltip";
import {
  TrackingPageContext,
  TrackingPageEnvironment,
} from "../../context/tracking-page-context";
import {
  getShipmentMapStatusLabel,
  type StatusLabelOverrides,
} from "./tracking-details-shared";

const GOOGLE_MAPS_PUBLIC_KEY = globalThis.GOOGLE_MAPS_PUBLIC_KEY ?? "";

const MAP_ID = "6e6e458248a438f94b2f4803";

const DEFAULT_PIN_COLOR = "#34a853";

const HIDDEN_MAP_STATUSES = new Set<OrderTrackingShipmentStatus>([
  OrderTrackingShipmentStatus.PRE_TRANSIT,
  OrderTrackingShipmentStatus.PENDING,
  OrderTrackingShipmentStatus.UNKNOWN,
]);

export function shouldHideTrackingMapForStatus(
  status: OrderTrackingShipmentStatus,
): boolean {
  return HIDDEN_MAP_STATUSES.has(status);
}

export interface TrackingMapStyle {
  pinColor?: string;
  pinShape?: TrackingMapPinShape;
  fontColor?: string;
  fontFamily?: string;
  heightPx?: number;
  zoom?: number;
}

interface MarkerProps {
  statusLabel: string | null;
  locationLabel: string | null;
  delivered: boolean;
  pinColor?: string;
  pinShape?: TrackingMapPinShape;
  fontColor?: string;
  fontFamily?: string;
}

export function getTrackingMapSrc(
  query: string,
  key: string = GOOGLE_MAPS_PUBLIC_KEY,
  zoom: number = DEFAULT_TRACKING_MAP_ZOOM,
): string | null {
  if (!key) {
    return null;
  }
  return `https://www.google.com/maps/embed/v1/place?key=${key}&q=${encodeURIComponent(
    query,
  )}&zoom=${zoom}`;
}

const EmbedTrackingMap = memo(function EmbedTrackingMap({
  query,
  height,
  zoom,
}: {
  query: string;
  height: number;
  zoom: number;
}) {
  const { t } = useTranslation();
  const src = useMemo(
    () => getTrackingMapSrc(query, GOOGLE_MAPS_PUBLIC_KEY, zoom),
    [query, zoom],
  );

  if (!src) {
    return null;
  }

  return (
    <iframe
      allowFullScreen={false}
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      src={src}
      style={{
        width: "100%",
        height,
        border: 0,
        borderRadius: "var(--border-radius-2xl)",
        display: "block",
      }}
      title={t("Shipment location map")}
    />
  );
});

const CheckGlyph = memo(function CheckGlyph() {
  return (
    <svg fill="none" height="14" viewBox="0 0 24 24" width="14">
      <path
        d="M5 13l4 4L19 7"
        stroke="#ffffff"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="3"
      />
    </svg>
  );
});

const PinGraphic = memo(function PinGraphic({
  color = DEFAULT_PIN_COLOR,
  shape = TrackingMapPinShape.DEFAULT,
  delivered,
}: {
  color?: string;
  shape?: TrackingMapPinShape;
  delivered: boolean;
}) {
  if (shape === TrackingMapPinShape.ROUNDED) {
    return (
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: "var(--border-radius-full)",
          background: color,
          border: "2px solid #ffffff",
          boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {delivered ? (
          <CheckGlyph />
        ) : (
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "var(--border-radius-lg)",
              background: "var(--redo-colors-background-bg-primary)",
            }}
          />
        )}
      </div>
    );
  }

  return (
    <svg fill="none" height="38" viewBox="0 0 24 32" width="28">
      <path
        d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20C24 5.373 18.627 0 12 0z"
        fill={color}
        stroke="#ffffff"
        strokeWidth="1.5"
      />
      {delivered ? (
        <path
          d="M7 12l3.2 3.2L17 8.5"
          stroke="#ffffff"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2.4"
        />
      ) : (
        <circle cx="12" cy="12" fill="#ffffff" r="4" />
      )}
    </svg>
  );
});

const MapStatusMarker = memo(function MapStatusMarker({
  position,
  statusLabel,
  locationLabel,
  pinColor,
  pinShape,
  fontColor,
  fontFamily,
  delivered,
}: { position: { lat: number; lng: number } } & MarkerProps) {
  const hasLabel = !!statusLabel || !!locationLabel;
  const [locationLabelRef, setLocationLabelRef] =
    useState<HTMLDivElement | null>(null);

  return (
    <AdvancedMarker position={position}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pointerEvents: "none",
        }}
      >
        {hasLabel && (
          <>
            <div
              style={{
                background: "var(--redo-colors-background-bg-primary)",
                borderRadius: "var(--border-radius-2xl)",
                padding: "var(--redo-spacing-md) var(--redo-spacing-xl)",
                boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
                textAlign: "center",
                maxWidth: 240,
                fontFamily: fontFamily || undefined,
                color: fontColor || "var(--redo-colors-text-text-primary)",
                pointerEvents: "auto",
              }}
            >
              {statusLabel && (
                <div
                  style={{
                    fontSize: "var(--redo-font-size-xs)",
                    fontWeight: "var(--redo-font-weight-semibold)",
                    opacity: 0.75,
                  }}
                >
                  {statusLabel}
                </div>
              )}
              {locationLabel && (
                <OverflowTooltip
                  overflowRef={locationLabelRef}
                  tooltipProps={{ title: locationLabel }}
                >
                  <div
                    ref={setLocationLabelRef}
                    style={{
                      fontSize: "var(--redo-font-size-md)",
                      fontWeight: "var(--redo-font-weight-bold)",
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {locationLabel}
                  </div>
                </OverflowTooltip>
              )}
            </div>
            <div
              style={{
                width: 0,
                height: 0,
                borderLeft: "6px solid transparent",
                borderRight: "6px solid transparent",
                borderTop: "7px solid #ffffff",
              }}
            />
          </>
        )}
        <div style={{ marginTop: hasLabel ? 4 : 0 }}>
          <PinGraphic color={pinColor} delivered={delivered} shape={pinShape} />
        </div>
      </div>
    </AdvancedMarker>
  );
});

const MapCanvas = memo(function MapCanvas({
  position,
  height,
  zoom,
  ...markerProps
}: {
  position: { lat: number; lng: number };
  height: number;
  zoom: number;
} & MarkerProps) {
  return (
    <div
      style={{
        width: "100%",
        height,
        borderRadius: "var(--border-radius-2xl)",
        overflow: "hidden",
      }}
    >
      <Map
        defaultCenter={position}
        defaultZoom={zoom}
        fullscreenControl={false}
        key={zoom}
        mapId={MAP_ID}
        mapTypeControl={false}
        streetViewControl={false}
        style={{ width: "100%", height: "100%" }}
      >
        <MapStatusMarker position={position} {...markerProps} />
      </Map>
    </div>
  );
});

interface GeocodeComponents {
  city?: string | null;
  state?: string | null;
  country?: string | null;
  postalCode?: string | null;
}

const ServerGeocodedMap = memo(function ServerGeocodedMap({
  components,
  fallbackQuery,
  geocode,
  height,
  zoom,
  ...markerProps
}: {
  components: GeocodeComponents;
  fallbackQuery: string;
  geocode: (
    location: GeocodeComponents,
  ) => Promise<{ latitude: number; longitude: number } | null>;
  height: number;
  zoom: number;
} & MarkerProps) {
  const componentsKey = JSON.stringify(components);
  const geoLoad = useLoad(async () => geocode(components), [componentsKey]);

  if (geoLoad.pending) {
    return null;
  }

  const geo = geoLoad.value;
  if (!geo || !GOOGLE_MAPS_PUBLIC_KEY) {
    return (
      <EmbedTrackingMap height={height} query={fallbackQuery} zoom={zoom} />
    );
  }

  const position = { lat: geo.latitude, lng: geo.longitude };

  return (
    <APIProvider apiKey={GOOGLE_MAPS_PUBLIC_KEY}>
      <MapCanvas
        height={height}
        position={position}
        zoom={zoom}
        {...markerProps}
      />
    </APIProvider>
  );
});

const TrackingMapAdvanced = memo(function TrackingMapAdvanced({
  orderId,
  shipmentId,
  fallbackQuery,
  geocodeTrackingLocation,
  height,
  zoom,
  ...markerProps
}: {
  orderId: string;
  shipmentId: string;
  fallbackQuery: string;
  geocodeTrackingLocation: (
    orderId: string,
    shipmentId: string,
  ) => Promise<{ latitude: number; longitude: number } | null>;
  height: number;
  zoom: number;
} & MarkerProps) {
  const geoLoad = useLoad(
    async () => geocodeTrackingLocation(orderId, shipmentId),
    [orderId, shipmentId],
  );

  if (geoLoad.pending) {
    return null;
  }

  const geo = geoLoad.value;
  if (!geo || !GOOGLE_MAPS_PUBLIC_KEY) {
    return (
      <EmbedTrackingMap height={height} query={fallbackQuery} zoom={zoom} />
    );
  }

  const position = { lat: geo.latitude, lng: geo.longitude };

  return (
    <APIProvider apiKey={GOOGLE_MAPS_PUBLIC_KEY}>
      <MapCanvas
        height={height}
        position={position}
        zoom={zoom}
        {...markerProps}
      />
    </APIProvider>
  );
});

export const TrackingMap = memo(function TrackingMap({
  shipment,
  order,
  mapStyle,
  statusLabelOverrides,
}: {
  shipment: TrackingPageShipment | undefined;
  order: TrackingPageOrder;
  mapStyle?: TrackingMapStyle;
  statusLabelOverrides?: StatusLabelOverrides;
}) {
  const context = useContext(TrackingPageContext);
  const location = useMemo(
    () => getTrackingMapLocation(shipment, order),
    [shipment, order],
  );
  const query = useMemo(() => locationToQuery(location), [location]);
  const locationLabel = useMemo(
    () => locationToCityStateLabel(location),
    [location],
  );
  const statusLabel = useMemo(
    () => getShipmentMapStatusLabel(shipment, statusLabelOverrides),
    [shipment, statusLabelOverrides],
  );

  if (!shipment || shouldHideTrackingMapForStatus(shipment.status)) {
    return null;
  }

  if (
    !query ||
    applyTrackingLocationRules(location, context?.textRules).matched
  ) {
    return null;
  }

  const height = mapStyle?.heightPx ?? DEFAULT_TRACKING_MAP_HEIGHT_PX;
  const zoom = mapStyle?.zoom ?? DEFAULT_TRACKING_MAP_ZOOM;

  if (context?.forceEmbedMap) {
    return <EmbedTrackingMap height={height} query={query} zoom={zoom} />;
  }

  if (!context?.jsMapsApiEnabled) {
    return null;
  }

  const markerProps: MarkerProps = {
    statusLabel,
    locationLabel,
    pinColor: mapStyle?.pinColor,
    pinShape: mapStyle?.pinShape,
    fontColor: mapStyle?.fontColor,
    fontFamily: mapStyle?.fontFamily,
    delivered: shipment.status === OrderTrackingShipmentStatus.DELIVERED,
  };

  if (
    context.environment === TrackingPageEnvironment.CUSTOMER_PORTAL &&
    context.geocodeTrackingLocation &&
    location &&
    order.internalMongoId
  ) {
    return (
      <TrackingMapAdvanced
        fallbackQuery={query}
        geocodeTrackingLocation={context.geocodeTrackingLocation}
        height={height}
        orderId={order.internalMongoId}
        shipmentId={shipment.id}
        zoom={zoom}
        {...markerProps}
      />
    );
  }

  if (context.geocodeMapLocation && location) {
    const components =
      typeof location === "string"
        ? { city: location }
        : {
            city: location.city,
            state: location.state,
            country: location.country,
            postalCode: location.postalCode,
          };
    return (
      <ServerGeocodedMap
        components={components}
        fallbackQuery={query}
        geocode={context.geocodeMapLocation}
        height={height}
        zoom={zoom}
        {...markerProps}
      />
    );
  }

  return <EmbedTrackingMap height={height} query={query} zoom={zoom} />;
});
